import { useRef, useState } from "react";
import {
  Upload,
  FileText,
  CheckCircle2,
  Loader2,
  Save,
  ExternalLink,
  RotateCcw,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { billOcrService } from "../services/bill-ocr.services";
import config from "@/lib/constants/config";

// Formats camelCase/camelCase keys into readable labels
function formatLabel(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function formatValue(key, value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value.length === 0
      ? "—"
      : value
          .map((v) => (typeof v === "object" ? JSON.stringify(v) : String(v)))
          .join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "—";
    return entries
      .map(([k, v]) => `${formatLabel(k)}: ${v ?? "—"}`)
      .join(" · ");
  }
  if (key.toLowerCase().includes("rm") && typeof value === "number") {
    return `RM ${value.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (key.toLowerCase().includes("kwh") && typeof value === "number") {
    return `${value.toLocaleString("en-MY")} kWh`;
  }
  if (key.toLowerCase().includes("kw") && typeof value === "number") {
    return `${value.toLocaleString("en-MY")} kW`;
  }
  return String(value);
}

// Fields to display prominently at the top
const HERO_FIELDS = [
  "facilityName",
  "billingMonth",
  "billingPeriodStart",
  "billingPeriodEnd",
  "jumlahBilRm",
  "cajSemasaRm",
  "consumptionKwh",
  "penggunaanKwh",
  "maxDemandKw",
  "noAkaun",
  "noInvois",
  "billDate",
  "invoiceNumber",
];

export function BillOcrPage() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [sendError, setSendError] = useState(null);

  const heroFields = extraction
    ? HERO_FIELDS.filter(
        (k) => extraction[k] !== undefined && extraction[k] !== null,
      )
    : [];
  const HIDDEN_FIELDS = new Set(["fieldSources", "extractionFailureReason"]);
  const detailFields = extraction
    ? Object.keys(extraction).filter(
        (k) => !HERO_FIELDS.includes(k) && !HIDDEN_FIELDS.has(k),
      )
    : [];

  const handleFile = (f) => {
    if (!f || f.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }
    setFile(f);
    setExtraction(null);
    setExtractError(null);
    setSendError(null);
    setSent(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    setExtractError(null);
    try {
      const data = await billOcrService.extract(file);
      setExtraction(data);
      toast.success("Bill extracted successfully");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Extraction failed. Please try again.";
      setExtractError(msg);
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  const handleSendToNxmap = async () => {
    if (!extraction) return;
    setSending(true);
    setSendError(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSent(true);
      toast.success("Bill data saved successfully");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save. Please try again.";
      setSendError(msg);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setExtraction(null);
    setExtractError(null);
    setSendError(null);
    setSent(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Bill Import (OCR)
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload a TNB electricity bill PDF to extract data and send it to
          NXMap.
        </p>
      </div>

      {/* Upload card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Upload Bill</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {/* Drop zone */}
          <div
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-6 transition-colors ${
              dragOver
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file ? (
              <>
                <FileText className="h-8 w-8 text-primary" />
                <span className="text-sm font-medium">{file.name}</span>
                <span className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">
                  Drop PDF here or click to browse
                </span>
                <span className="text-xs text-muted-foreground">
                  TNB Bil Elektrik — PDF only
                </span>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />

          <div className="flex items-center gap-3">
            <Button
              onClick={handleExtract}
              disabled={!file || extracting || !!extraction}
            >
              {extracting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {extracting ? "Extracting…" : "Extract Bill"}
            </Button>
            {(file || extraction) && (
              <Button variant="ghost" size="sm" onClick={handleReset}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Reset
              </Button>
            )}
          </div>
          {extractError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {extractError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Extraction preview */}
      {extraction && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">2. Extracted Data</CardTitle>
            <Badge variant="secondary" className="text-xs">
              {Object.keys(extraction).length} fields
            </Badge>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Hero fields */}
            {heroFields.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {heroFields.map((key) =>
                  key === "facilityName" ? (
                    <div
                      key={key}
                      className="col-span-2 rounded-md border border-primary/30 bg-primary/5 px-4 py-3"
                    >
                      <p className="text-[11px] font-medium uppercase tracking-wide text-primary/70">
                        {formatLabel(key)}
                      </p>
                      <p className="mt-0.5 text-base font-bold text-primary">
                        {formatValue(key, extraction[key])}
                      </p>
                    </div>
                  ) : (
                    <div
                      key={key}
                      className="rounded-md border bg-muted/30 px-3 py-2"
                    >
                      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {formatLabel(key)}
                      </p>
                      <p className="mt-0.5 text-sm font-semibold">
                        {formatValue(key, extraction[key])}
                      </p>
                    </div>
                  ),
                )}
              </div>
            )}

            {/* Detail fields */}
            {detailFields.length > 0 && (
              <>
                <Separator />
                <div className="grid grid-cols-1 gap-y-2 gap-x-3 sm:grid-cols-2">
                  {detailFields.map((key) => {
                    const val = extraction[key];
                    const isNested =
                      val !== null &&
                      typeof val === "object" &&
                      !Array.isArray(val);
                    if (isNested) {
                      return (
                        <div
                          key={key}
                          className="col-span-full flex flex-col gap-1 text-sm"
                        >
                          <span className="text-muted-foreground">
                            {formatLabel(key)}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(val).map(([k, v]) => (
                              <span
                                key={k}
                                className="rounded-md border bg-muted/40 px-2 py-0.5 text-xs font-medium"
                              >
                                {formatLabel(k)}:{" "}
                                <span className="text-muted-foreground">
                                  {String(v ?? "—")}
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={key} className="flex items-start gap-2 text-sm">
                        <span className="shrink-0 text-muted-foreground">
                          {formatLabel(key)}
                        </span>
                        <span className="min-w-0 flex-1 break-words text-right font-medium">
                          {formatValue(key, val)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <Separator />

            {/* Accept & Save */}
            {sent ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Saved to server
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(config.nxmapBaseUrl, "_blank")}
                >
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                  Show on NXMap
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Button
                  size="lg"
                  className="self-start px-10"
                  onClick={handleSendToNxmap}
                  disabled={sending}
                >
                  {sending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  {sending ? "Saving…" : "Accept and Save to Server"}
                </Button>
                {sendError && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {sendError}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
